import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { trackCompleteRegistration } from '../utils/metaPixel';

const AuthContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 48px;
  margin-bottom: 48px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const AuthTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const AuthSubtitle = styled.p`
  font-size: 1rem;
  color: #495057;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  color: #fa5252;
  background-color: #fff5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
`;

const AuthFooter = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 0.875rem;
  color: ${COLORS.PRIMARY};

  
  a {
    color: ${COLORS.PRIMARY};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordRequirements = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 8px 0 0 0;
  font-size: 0.75rem;
  color: #868e96;
`;

const Requirement = styled.li<{ met: boolean }>`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  color: ${props => props.met ? '#40c057' : '#868e96'};
  
  svg {
    margin-right: 4px;
  }
`;
 
const ConsentContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const ConsentLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${COLORS.TEXT};
`;

// Invisible real checkbox
const ConsentCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid ${COLORS.PRIMARY};
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:checked {
    background-color: ${COLORS.PRIMARY};
    border-color: ${COLORS.PRIMARY};
  }

  /* Checkmark icon */
  &:checked::after {
    content: '✓';
    position: absolute;
    color: white;
    font-size: 14px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const RegisterPage: React.FC = () => {
  const { register, isLoggedIn, error: authError, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Password requirements state
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false);

  // age control
  const [over18, setOver18] = useState(false);
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);
  
  // Password strength checks
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!username.trim()) {
      setError(t('register.errors.usernameRequired'));
      return;
    }
    
    if (!email.trim()) {
      setError(t('register.errors.emailRequired'));
      return;
    }
    
    if (!password) {
      setError(t('register.errors.passwordRequired'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('register.errors.passwordsDoNotMatch'));
      return;
    }
    
    // Check password strength
    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      setError(t('register.errors.passwordRequirements'));
      return;
    }
    
    const success = await register(username, email, password);

    if (success) {
      // Fires only on a confirmed new account, before we leave the page
      trackCompleteRegistration('email');
      navigate('/');
    }
  };
  
  return (
    <AuthContainer>
      <AuthHeader>
        <AuthTitle>{t('register.title')}</AuthTitle>
        <AuthSubtitle>{t('register.subtitle')}</AuthSubtitle>
      </AuthHeader>

      <GoogleLoginButton></GoogleLoginButton>
      
      {(error || authError) && (
        <ErrorMessage>
          {error || authError}
        </ErrorMessage>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Input
          label={t('register.form.username.label')}
          placeholder={t('register.form.username.placeholder')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        
        <Input
          label={t('register.form.email.label')}
          type="email"
          placeholder={t('register.form.email.label')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        
        <div>
          <Input
            label={t('register.form.password.label')}
            type="password"
            placeholder={t('register.form.password.label')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
          />
          
          {passwordFocused && (
            <PasswordRequirements>
              <Requirement met={hasMinLength}>
                {hasMinLength ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                )}
                {t('register.passwordRequirements.minLength')}
              </Requirement>
              
              <Requirement met={hasUppercase}>
                {hasUppercase ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                )}
                {t('register.passwordRequirements.uppercase')}
              </Requirement>
              
              <Requirement met={hasLowercase}>
                {hasLowercase ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                )}
                {t('register.passwordRequirements.lowercase')}
              </Requirement>
              
              <Requirement met={hasNumber}>
                {hasNumber ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                )}
               {t('register.passwordRequirements.number')}
              </Requirement>
              
              <Requirement met={hasSpecialChar}>
                {hasSpecialChar ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                )}
                {t('register.passwordRequirements.specialChar')}
              </Requirement>
            </PasswordRequirements>
          )}
        </div>
        
        <Input
          label={t('register.form.confirmPassword.label')}
          type="password"
          placeholder={t('register.form.confirmPassword.placeholder')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword && password !== confirmPassword ? t('register.errors.passwordsDoNotMatch') : undefined}
        />

        <ConsentContainer>
          <ConsentLabel>
            <ConsentCheckbox
              checked={over18}
              onChange={(e) => setOver18(e.target.checked)}
            />
            {t('register.form.over18Consent', 'I confirm I am over 18')}
          </ConsentLabel>
        </ConsentContainer>
        
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          isLoading={loading}
          disabled={!over18}
        >
          {t('register.form.submitButton')}
        </Button>
      </Form>
      
      <AuthFooter>
        {t('register.footer.alreadyHaveAccount')} <Link to="/login">{t('register.footer.signIn')}</Link>
      </AuthFooter>
    </AuthContainer>
  );
};